import React, { useEffect, useMemo, useRef, useState } from "react";
import { RotateCw, Minus, Plus, Zap, Sparkles, Lightbulb, FileDown } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Every raw stat the coach types / adjusts. `gp` is games-played multiplier so the
// derived numbers can represent either a per-game view (gp=1) or an aggregate.
const EMPTY = { gp: 1, min: 0, fgm2: 0, fga2: 0, fgm3: 0, fga3: 0, ftm: 0, fta: 0, tov: 0 };

const n = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);

function derive(raw) {
  const gp = Math.max(1, n(raw.gp));
  const _min = n(raw.min);
  const _2m = n(raw.fgm2), _2a = n(raw.fga2), _3m = n(raw.fgm3), _3a = n(raw.fga3);
  const _ftm = n(raw.ftm), _fta = n(raw.fta), _to = n(raw.tov);
  const fgm = _2m + _3m, fga = _2a + _3a;
  const pts = _2m * 2 + _3m * 3 + _ftm;
  const fgPts = _2m * 2 + _3m * 3;
  const poss = fga + 0.44 * _fta + _to;
  return {
    pts, fgm, fga, poss,
    ppp:  poss > 0 ? pts / poss : null,
    pps:  fga  > 0 ? fgPts / fga : null,
    pps2: _2a  > 0 ? (_2m * 2) / _2a : null,
    pps3: _3a  > 0 ? (_3m * 3) / _3a : null,
    fg2:  _2a  > 0 ? _2m / _2a : null,
    fg3:  _3a  > 0 ? _3m / _3a : null,
    ftPct:_fta > 0 ? _ftm / _fta : null,
    efg:  fga  > 0 ? (fgm + 0.5 * _3m) / fga : null,
    ts:   (fga + 0.44 * _fta) > 0 ? pts / (2 * (fga + 0.44 * _fta)) : null,
    ptsPerGame:  pts / gp,
    minPerGame:  _min / gp,
    possPerGame: poss / gp,
    // Per-game usage split by shot type (for the Usage inputs).
    fga2PerGame: _2a / gp,
    fga3PerGame: _3a / gp,
    ftaPerGame:  _fta / gp,
  };
}

function seedFromSeasonAverages(sa) {
  if (!sa) return null;
  const fgm  = n(sa.fgm),  fga  = n(sa.fga);
  const fg3m = n(sa.fg3m), fg3a = n(sa.fg3a);
  const ftm  = n(sa.ftm),  fta  = n(sa.fta);
  const tov  = sa.turnover ?? sa.tov ?? 0;
  const min  = sa.min ?? sa.mins ?? sa.minutes ?? null;
  if (!fga || !fg3a) return null;
  return {
    gp: 1,
    min:  min !== null && min !== undefined ? Number(min).toFixed(1) : 0,
    fgm2: Math.max(0, fgm - fg3m).toFixed(2),
    fga2: Math.max(0, fga - fg3a).toFixed(2),
    fgm3: Number(fg3m).toFixed(2),
    fga3: Number(fg3a).toFixed(2),
    ftm:  Number(ftm).toFixed(2),
    fta:  Number(fta).toFixed(2),
    tov:  Number(tov).toFixed(2),
  };
}

const fmt = (v, d = 2) => (v === null || v === undefined ? "—" : Number(v).toFixed(d));

export default function PlayerProjections({ seasonAverages, playerName }) {
  const [actual, setActual] = useState(EMPTY);
  const [projected, setProjected] = useState(EMPTY);
  const [seeded, setSeeded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);

  const dActual    = useMemo(() => derive(actual),    [actual]);
  const dProjected = useMemo(() => derive(projected), [projected]);

  const loadFromSeasonAvg = () => {
    const seed = seedFromSeasonAverages(seasonAverages);
    if (!seed) {
      toast.error("No season averages available", { description: "Enter values manually below." });
      return;
    }
    setActual(seed);
    setProjected(seed);
    setSeeded(true);
    toast.success("Seeded from season averages");
  };

  const resetProjection = () => { setProjected(actual); toast.info("Projection reset to actuals"); };
  const copyToProjection = () => { setProjected(actual); toast.success("Copied actuals → projection"); };

  const sharePDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(printRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 36;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;
      // Scale-to-fit ONE page (contain): honor both width and height limits, then center.
      const scale = Math.min(maxW / canvas.width, maxH / canvas.height);
      const finalW = canvas.width * scale;
      const finalH = canvas.height * scale;
      pdf.addImage(img, "PNG", (pageW - finalW) / 2, (pageH - finalH) / 2, finalW, finalH);
      const safeName = (playerName || "Player").replace(/\s+/g, "-");
      const dateStr = new Date().toISOString().slice(0, 10);
      pdf.save(`${safeName}-Projection-${dateStr}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed", { description: e.message });
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="player-projections">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-[var(--org-accent)]" />
          <div>
            <h2 className="font-heading uppercase text-2xl tracking-[0.2em] text-white">Projections</h2>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#a0a0a0] mt-0.5">What-if efficiency calculator</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadFromSeasonAvg}
            className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white text-[10px] uppercase tracking-[0.3em] font-bold hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"
            data-testid="proj-load-season-btn">
            <Zap className="w-3 h-3" /> Load from Season Avg
          </button>
          <button
            onClick={sharePDF}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60"
            data-testid="proj-share-pdf-btn">
            <FileDown className="w-3 h-3" /> {exporting ? "Exporting…" : "Share as PDF"}
          </button>
        </div>
      </div>

      {/* ===== ACTUALS ===== */}
      <div className="mb-6">
        <StatBlock title="Actuals" testPrefix="actual" raw={actual} setRaw={setActual} d={dActual} />
      </div>

      {/* ===== PROJECTION ===== */}
      <div className="border-t border-[var(--org-accent)]/30 pt-6" data-testid="proj-section">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--org-accent)] font-bold">Projection · adjust inputs to see impact</div>
          <div className="flex items-center gap-2">
            <button onClick={copyToProjection}
              className="flex items-center gap-2 px-3 py-1.5 border border-white/10 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white hover:border-white/30"
              data-testid="proj-copy-btn">
              Sync ← Actuals
            </button>
            <button onClick={resetProjection}
              className="flex items-center gap-2 px-3 py-1.5 border border-white/10 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white hover:border-white/30"
              data-testid="proj-reset-btn">
              <RotateCw className="w-3 h-3" /> Reset
            </button>
          </div>
        </div>
        <StatBlock title="Projection" testPrefix="proj" raw={projected} setRaw={setProjected} d={dProjected} baseline={dActual} playerName={playerName} withSteppers />
        <Takeaway actual={actual} projected={projected} dActual={dActual} dProjected={dProjected} playerName={playerName} />
      </div>

      {/* Offscreen printable summary — rendered off-viewport, captured by html2canvas for the Share as PDF export. */}
      <div style={{ position: "fixed", left: "-10000px", top: 0, width: "800px", pointerEvents: "none" }} aria-hidden="true">
        <PrintableSummary ref={printRef} playerName={playerName} actual={actual} projected={projected} dActual={dActual} dProjected={dProjected} />
      </div>
    </section>
  );
}

// Light-mode printable layout for the PDF export. Simple palette so it renders cleanly
// on paper and doesn't burn the parent's ink budget. Uses inline styles so html2canvas
// captures it identically no matter what CSS is loaded.
const PrintableSummary = React.forwardRef(function PrintableSummary({ playerName, actual, projected, dActual, dProjected }, ref) {
  const firstName = (playerName || "Player").split(" ")[0];
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const Row = ({ label, actualVal, projVal, format = "num", digits = 1 }) => {
    const disp = (v) => v === null || v === undefined ? "—" : format === "pct" ? `${(v * 100).toFixed(1)}%` : Number(v).toFixed(digits);
    const delta = (actualVal !== null && actualVal !== undefined && projVal !== null && projVal !== undefined) ? projVal - actualVal : null;
    const showDelta = delta !== null && Math.abs(delta) >= (format === "pct" ? 0.001 : 0.05);
    const deltaColor = delta > 0 ? "#15803d" : "#b91c1c";
    return (
      <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
        <td style={{ padding: "10px 14px", fontSize: 13, color: "#111", fontWeight: 600 }}>{label}</td>
        <td style={{ padding: "10px 14px", fontSize: 14, color: "#374151", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{disp(actualVal)}</td>
        <td style={{ padding: "10px 14px", fontSize: 14, color: "#111", textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{disp(projVal)}</td>
        <td style={{ padding: "10px 14px", fontSize: 13, textAlign: "right", color: deltaColor, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
          {showDelta ? (format === "pct" ? `${delta > 0 ? "+" : ""}${(delta * 100).toFixed(1)}%` : `${delta > 0 ? "+" : ""}${delta.toFixed(2)}`) : ""}
        </td>
      </tr>
    );
  };

  return (
    <div ref={ref} style={{ background: "#ffffff", padding: 32, fontFamily: "Helvetica, Arial, sans-serif" }}>
      {/* Header */}
      <div style={{ borderBottom: "3px solid #d8b854", paddingBottom: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 11, letterSpacing: 3, color: "#6b7280", textTransform: "uppercase", fontWeight: 700 }}>Player Projection Report</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: "#111", marginTop: 4 }}>{firstName}'s Future Stats</div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{playerName || ""} · {today}</div>
      </div>

      {/* Stats table */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 24 }}>
        <thead>
          <tr style={{ background: "#f3f4f6" }}>
            <th style={{ padding: "10px 14px", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#4b5563", textAlign: "left" }}>Stat</th>
            <th style={{ padding: "10px 14px", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#4b5563", textAlign: "right" }}>Actual</th>
            <th style={{ padding: "10px 14px", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#4b5563", textAlign: "right" }}>Projected</th>
            <th style={{ padding: "10px 14px", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#4b5563", textAlign: "right" }}>Change</th>
          </tr>
        </thead>
        <tbody>
          <Row label="Points / Game"        actualVal={dActual.ptsPerGame} projVal={dProjected.ptsPerGame} />
          <Row label="Minutes / Game"       actualVal={dActual.minPerGame} projVal={dProjected.minPerGame} />
          <Row label="Points Per Possession" actualVal={dActual.ppp} projVal={dProjected.ppp} digits={2} />
          <Row label="Points Per Shot"      actualVal={dActual.pps} projVal={dProjected.pps} digits={2} />
          <Row label="2FG%"                 actualVal={dActual.fg2}  projVal={dProjected.fg2}  format="pct" />
          <Row label="3FG%"                 actualVal={dActual.fg3}  projVal={dProjected.fg3}  format="pct" />
          <Row label="FT%"                  actualVal={dActual.ftPct} projVal={dProjected.ftPct} format="pct" />
          <Row label="eFG%"                 actualVal={dActual.efg}  projVal={dProjected.efg}  format="pct" />
          <Row label="True Shooting %"      actualVal={dActual.ts}   projVal={dProjected.ts}   format="pct" />
          <Row label="2PT PPS"              actualVal={dActual.pps2} projVal={dProjected.pps2} digits={2} />
          <Row label="3PT PPS"              actualVal={dActual.pps3} projVal={dProjected.pps3} digits={2} />
        </tbody>
      </table>

      {/* Coaching Takeaway — reuse the same logic, but restyle for print */}
      <PrintableTakeaway actual={actual} projected={projected} dActual={dActual} dProjected={dProjected} playerName={playerName} />

      {/* Footer */}
      <div style={{ marginTop: 32, paddingTop: 16, borderTop: "1px solid #e5e7eb", fontSize: 10, color: "#9ca3af", textAlign: "center" }}>
        Maximize Analytics · Coaching projection based on current season averages.
      </div>
    </div>
  );
});

// Auto-writes a plain-English coaching summary comparing projected → actuals.
// Only surfaces when at least one raw input has meaningfully shifted.
function buildTakeawayText({ actual, projected, dActual, dProjected, playerName }) {
  const name = (playerName || "").split(" ")[0] || "The player";
  const gA = Math.max(1, n(actual.gp)), gP = Math.max(1, n(projected.gp));
  const perDelta = (key) => n(projected[key]) / gP - n(actual[key]) / gA;

  const changes = [];
  const d3m = perDelta("fgm3"), d3a = perDelta("fga3");
  const d2m = perDelta("fgm2"), d2a = perDelta("fga2");
  const dFTM = perDelta("ftm"), dTov = perDelta("tov"), dMin = perDelta("min");

  const num = (v) => (Math.abs(v) >= 10 ? v.toFixed(0) : v.toFixed(1));
  const push = (verb, val, noun) => {
    const dir = val > 0 ? "more" : "fewer";
    changes.push(`${verb} ${num(Math.abs(val))} ${dir} ${noun}s per game`);
  };
  if (Math.abs(d3m) >= 0.4)      push("hits",     d3m, "3-point make");
  else if (Math.abs(d3a) >= 0.4) push("takes",    d3a, "3-point attempt");
  if (Math.abs(d2m) >= 0.4)      push("converts", d2m, "2-point make");
  else if (Math.abs(d2a) >= 0.4) push("takes",    d2a, "2-point attempt");
  if (Math.abs(dFTM) >= 0.4)     push("hits",     dFTM, "free-throw make");
  if (Math.abs(dTov) >= 0.4)     push("commits",  dTov, "turnover");
  if (Math.abs(dMin) >= 1.0)     push("plays",    dMin, "minute");

  if (changes.length === 0) return null;

  const dPPG = (dProjected.ptsPerGame ?? 0) - (dActual.ptsPerGame ?? 0);
  const dTS  = (dProjected.ts ?? 0)         - (dActual.ts ?? 0);
  const dPPP = (dProjected.ppp ?? 0)        - (dActual.ppp ?? 0);
  const dEFG = (dProjected.efg ?? 0)        - (dActual.efg ?? 0);

  const impacts = [];
  if (Math.abs(dPPG) >= 0.1) impacts.push(`scoring ${dPPG > 0 ? "climbs" : "drops"} from ${dActual.ptsPerGame.toFixed(1)} to ${dProjected.ptsPerGame.toFixed(1)} PPG (${dPPG > 0 ? "+" : ""}${dPPG.toFixed(1)})`);
  if (Math.abs(dTS) >= 0.005 && dProjected.ts !== null) impacts.push(`TS% ${dTS > 0 ? "jumps" : "dips"} to ${(dProjected.ts * 100).toFixed(1)}% (${dTS > 0 ? "+" : ""}${(dTS * 100).toFixed(1)}%)`);
  else if (Math.abs(dEFG) >= 0.005 && dProjected.efg !== null) impacts.push(`eFG% shifts to ${(dProjected.efg * 100).toFixed(1)}%`);
  if (Math.abs(dPPP) >= 0.03 && dProjected.ppp !== null) impacts.push(`PPP ${dPPP > 0 ? "rises" : "falls"} to ${dProjected.ppp.toFixed(2)} (${dPPP > 0 ? "+" : ""}${dPPP.toFixed(2)})`);

  const join = (arr) => arr.length <= 1 ? (arr[0] || "") : arr.slice(0, -1).join(", ") + " and " + arr[arr.length - 1];
  return { name, changeText: join(changes), impactText: join(impacts) };
}

function Takeaway({ actual, projected, dActual, dProjected, playerName }) {
  const t = buildTakeawayText({ actual, projected, dActual, dProjected, playerName });
  if (!t) return null;
  return (
    <div className="mt-6 border border-[var(--org-accent)]/40 bg-[var(--org-accent)]/5 p-5" data-testid="proj-takeaway">
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb className="w-4 h-4 text-[var(--org-accent)]" />
        <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--org-accent)]">Coaching Takeaway</div>
      </div>
      <div className="text-[15px] text-white leading-relaxed">
        If <span className="text-[var(--org-accent)] font-bold">{t.name}</span> {t.changeText}
        {t.impactText ? <>, {t.impactText}</> : null}.
      </div>
    </div>
  );
}

function PrintableTakeaway({ actual, projected, dActual, dProjected, playerName }) {
  const t = buildTakeawayText({ actual, projected, dActual, dProjected, playerName });
  if (!t) return null;
  return (
    <div style={{ background: "#fdf6df", border: "1px solid #d8b854", padding: "16px 20px", borderRadius: 4 }}>
      <div style={{ fontSize: 10, letterSpacing: 3, fontWeight: 700, color: "#8a7020", textTransform: "uppercase", marginBottom: 6 }}>Coaching Takeaway</div>
      <div style={{ fontSize: 14, color: "#1f2937", lineHeight: 1.6 }}>
        If <strong style={{ color: "#8a7020" }}>{t.name}</strong> {t.changeText}{t.impactText ? `, ${t.impactText}` : ""}.
      </div>
    </div>
  );
}

function StatBlock({ title, testPrefix, raw, setRaw, d, baseline, withSteppers, playerName }) {
  const upd = (k, v) => setRaw((r) => ({ ...r, [k]: v }));
  const step = (k, delta) => setRaw((r) => ({ ...r, [k]: Math.max(0, +(n(r[k]) + delta).toFixed(2)) }));
  // Change minutes → scale every counting stat (attempts, makes, TO) by the same ratio.
  // Coaches use this to answer: "what happens if she plays 30 mins vs 25?"
  const scaleByMin = (newMinVal) => setRaw((r) => {
    const cur = n(r.min);
    const newMin = Math.max(0, +Number(newMinVal).toFixed(1));
    if (cur <= 0) return { ...r, min: newMin };
    const ratio = newMin / cur;
    return {
      ...r,
      min: newMin,
      fgm2: +(n(r.fgm2) * ratio).toFixed(2),
      fga2: +(n(r.fga2) * ratio).toFixed(2),
      fgm3: +(n(r.fgm3) * ratio).toFixed(2),
      fga3: +(n(r.fga3) * ratio).toFixed(2),
      ftm:  +(n(r.ftm)  * ratio).toFixed(2),
      fta:  +(n(r.fta)  * ratio).toFixed(2),
      tov:  +(n(r.tov)  * ratio).toFixed(2),
    };
  });
  // Only the projection block gets the auto-scaling behavior on min. Actuals stay direct-edit.
  const updMin  = withSteppers ? (_k, v)     => { const p = parseFloat(v); if (!Number.isNaN(p)) scaleByMin(p); } : upd;
  const stepMin = withSteppers ? (_k, delta) => scaleByMin(n(raw.min) + delta) : step;
  // Change a shooting % → recompute makes from the current attempts (usage stays the same).
  const setPct = (type, newPctText) => {
    const p = parseFloat(newPctText);
    if (Number.isNaN(p)) return;
    const pct = Math.max(0, Math.min(100, p)) / 100;
    setRaw((r) => {
      if (type === "2") {
        const a = n(r.fga2); if (a <= 0) return r;
        return { ...r, fgm2: +(a * pct).toFixed(2) };
      }
      if (type === "3") {
        const a = n(r.fga3); if (a <= 0) return r;
        return { ...r, fgm3: +(a * pct).toFixed(2) };
      }
      if (type === "ft") {
        const a = n(r.fta); if (a <= 0) return r;
        return { ...r, ftm: +(a * pct).toFixed(2) };
      }
      return r;
    });
  };
  // Change per-game attempts → scale attempts to that new usage AND scale makes to keep % constant.
  const setAttPerGame = (type, newAttPGText) => {
    const p = parseFloat(newAttPGText);
    if (Number.isNaN(p) || p < 0) return;
    setRaw((r) => {
      const g = Math.max(1, n(r.gp));
      const newTot = p * g;
      const scale = (curM, curA) => (curA > 0 ? (curM / curA) * newTot : 0);
      if (type === "2") return { ...r, fga2: +newTot.toFixed(2), fgm2: +scale(n(r.fgm2), n(r.fga2)).toFixed(2) };
      if (type === "3") return { ...r, fga3: +newTot.toFixed(2), fgm3: +scale(n(r.fgm3), n(r.fga3)).toFixed(2) };
      if (type === "ft") return { ...r, fta:  +newTot.toFixed(2), ftm:  +scale(n(r.ftm),  n(r.fta )).toFixed(2) };
      return r;
    });
  };

  return (
    <div className="border border-white/10 bg-[#141414] p-6" data-testid={`proj-block-${testPrefix}`}>
      {/* GAMES */}
      <SectionHead>Games</SectionHead>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Field k="gp"  label="Games Played" raw={raw} upd={upd}    step={step}    testPrefix={testPrefix} withSteppers={withSteppers} />
        <Field k="min" label={withSteppers ? "Minutes · scales everything" : "Minutes"} raw={raw} upd={updMin} step={stepMin} testPrefix={testPrefix} withSteppers={withSteppers} />
      </div>

      {/* FIELD GOALS */}
      <SectionHead>Field Goals</SectionHead>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Field k="fgm2" label="2FGM" raw={raw} upd={upd} step={step} testPrefix={testPrefix} withSteppers={withSteppers} />
        <Field k="fga2" label="2FGA" raw={raw} upd={upd} step={step} testPrefix={testPrefix} withSteppers={withSteppers} />
        <Field k="fgm3" label="3FGM" raw={raw} upd={upd} step={step} testPrefix={testPrefix} withSteppers={withSteppers} />
        <Field k="fga3" label="3FGA" raw={raw} upd={upd} step={step} testPrefix={testPrefix} withSteppers={withSteppers} />
      </div>

      {/* FREE THROWS & TURNOVERS */}
      <SectionHead>Free Throws &amp; Turnovers</SectionHead>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Field k="ftm" label="FTM" raw={raw} upd={upd} step={step} testPrefix={testPrefix} withSteppers={withSteppers} />
        <Field k="fta" label="FTA" raw={raw} upd={upd} step={step} testPrefix={testPrefix} withSteppers={withSteppers} />
        <Field k="tov" label="TO"  raw={raw} upd={upd} step={step} testPrefix={testPrefix} withSteppers={withSteppers} />
      </div>

      {/* USAGE — projection-only shortcut. Editing per-game attempts locks the % and scales makes. */}
      {withSteppers && (
        <>
          <SectionHead>Usage · lock efficiency, adjust attempts per game</SectionHead>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <NumInput label="2PT ATT / G" derived={d.fga2PerGame} baseline={baseline?.fga2PerGame} onCommit={(v) => setAttPerGame("2", v)}  testId={`proj-att2-${testPrefix}`} />
            <NumInput label="3PT ATT / G" derived={d.fga3PerGame} baseline={baseline?.fga3PerGame} onCommit={(v) => setAttPerGame("3", v)}  testId={`proj-att3-${testPrefix}`} />
            <NumInput label="FT ATT / G"  derived={d.ftaPerGame}  baseline={baseline?.ftaPerGame}  onCommit={(v) => setAttPerGame("ft", v)} testId={`proj-attft-${testPrefix}`} />
          </div>
        </>
      )}

      {/* SHOOTING RATES — projection-only shortcut. Editing a % locks the attempts and recomputes makes. */}
      {withSteppers && (
        <>
          <SectionHead>Shooting Rates · lock usage, adjust efficiency</SectionHead>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <PctInput label="2FG%" derived={d.fg2}   baseline={baseline?.fg2}   onCommit={(v) => setPct("2", v)}  disabled={n(raw.fga2) === 0} testId={`proj-pct2-${testPrefix}`} />
            <PctInput label="3FG%" derived={d.fg3}   baseline={baseline?.fg3}   onCommit={(v) => setPct("3", v)}  disabled={n(raw.fga3) === 0} testId={`proj-pct3-${testPrefix}`} />
            <PctInput label="FT%"  derived={d.ftPct} baseline={baseline?.ftPct} onCommit={(v) => setPct("ft", v)} disabled={n(raw.fta) === 0}  testId={`proj-pctft-${testPrefix}`} />
          </div>
        </>
      )}

      {/* HEADLINE CARDS — PPP & PPS */}
      {withSteppers && (
        <div className="mb-4 mt-2">
          <div className="font-heading uppercase text-xl tracking-[0.2em] text-white" data-testid="proj-future-stats-header">
            {(playerName || "Player").split(" ")[0]}'s Future Stats
          </div>
          <div className="h-0.5 w-16 bg-[var(--org-accent)] mt-2" />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <BigCard label="Points Per Possession" value={d.ppp} format="num" baseline={baseline?.ppp} formula="PTS ÷ (FGA + 0.44 × FTA + TO)" testId={`proj-ppp-${testPrefix}`} />
        <BigCard label="Points Per Shot"        value={d.pps} format="num" baseline={baseline?.pps} formula="Field-goal points ÷ FGA (excludes FT & TO)" testId={`proj-pps-${testPrefix}`} />
      </div>

      {/* SECONDARY ROW — Total Points | PPG | MPG | Possessions Used */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
        <SmallCard label="Total Points"     value={d.pts}         format="num" digits={1} baseline={baseline?.pts}         testId={`proj-pts-${testPrefix}`} />
        <SmallCard label="Points / Game"    value={d.ptsPerGame}  format="num" digits={1} baseline={baseline?.ptsPerGame}  testId={`proj-ppg-${testPrefix}`} />
        <SmallCard label="Minutes / Game"   value={d.minPerGame}  format="num" digits={1} baseline={baseline?.minPerGame}  testId={`proj-mpg-${testPrefix}`} />
        <SmallCard label="Possessions Used" value={d.poss}        format="num"            baseline={baseline?.poss}        testId={`proj-poss-${testPrefix}`} />
      </div>

      {/* SECONDARY ROW — 2PT PPS | 3PT PPS */}
      <div className="grid grid-cols-2 gap-4 mb-3">
        <SmallCard label="2PT PPS" value={d.pps2} format="num" baseline={baseline?.pps2} testId={`proj-pps2-${testPrefix}`} />
        <SmallCard label="3PT PPS" value={d.pps3} format="num" baseline={baseline?.pps3} testId={`proj-pps3-${testPrefix}`} />
      </div>

      {/* TERTIARY — shooting percentages */}
      <div className="grid grid-cols-3 gap-4">
        <SmallCard label="2P%" value={d.fg2}   format="pct" baseline={baseline?.fg2}   testId={`proj-fg2-${testPrefix}`} />
        <SmallCard label="3P%" value={d.fg3}   format="pct" baseline={baseline?.fg3}   testId={`proj-fg3-${testPrefix}`} />
        <SmallCard label="FT%" value={d.ftPct} format="pct" baseline={baseline?.ftPct} testId={`proj-ft-${testPrefix}`} />
      </div>

      {/* eFG & TS row */}
      <div className="grid grid-cols-2 gap-4 mt-3">
        <SmallCard label="eFG%" value={d.efg} format="pct" baseline={baseline?.efg} testId={`proj-efg-${testPrefix}`} />
        <SmallCard label="TS%"  value={d.ts}  format="pct" baseline={baseline?.ts}  testId={`proj-ts-${testPrefix}`} />
      </div>
    </div>
  );
}

function SectionHead({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--org-accent)] mb-3">{children}</div>;
}

function Field({ k, label, raw, upd, step, testPrefix, withSteppers, span = 1 }) {
  return (
    <div className={`col-span-${span}`}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] font-bold mb-2">{label}</div>
      <div className={`flex items-stretch ${withSteppers ? "gap-1" : ""}`}>
        {withSteppers && (
          <button onClick={() => step(k, -1)} className="w-9 border border-white/10 bg-[#101010] text-[#a0a0a0] hover:text-white hover:border-white/30 flex items-center justify-center" data-testid={`proj-${k}-minus-${testPrefix}`}>
            <Minus className="w-3 h-3" />
          </button>
        )}
        <input
          type="number"
          value={raw[k]}
          onChange={(e) => upd(k, e.target.value)}
          className="flex-1 bg-[#0f0f0f] border border-white/10 px-3 py-2 text-lg font-heading text-white focus:outline-none focus:border-[var(--org-accent)] text-center"
          data-testid={`proj-${k}-input-${testPrefix}`}
        />
        {withSteppers && (
          <button onClick={() => step(k, +1)} className="w-9 border border-white/10 bg-[#101010] text-[#a0a0a0] hover:text-white hover:border-white/30 flex items-center justify-center" data-testid={`proj-${k}-plus-${testPrefix}`}>
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// Rate input for the projection block. Local state so the user can type freely; commits on blur or Enter.
// When the underlying `derived` value shifts externally (e.g. user bumps 3FGM directly, or clicks Sync/Reset),
// we mirror it back into the input as long as it isn't focused.
function PctInput({ label, derived, baseline, onCommit, disabled, testId }) {
  return <ValueInput label={label} derived={derived} baseline={baseline} onCommit={onCommit} disabled={disabled} testId={testId} format="pct" suffix="%" step="0.1" />;
}

// Generic numeric input (usage / attempts per game). Same commit-on-blur behavior as PctInput.
function NumInput({ label, derived, baseline, onCommit, disabled, testId }) {
  return <ValueInput label={label} derived={derived} baseline={baseline} onCommit={onCommit} disabled={disabled} testId={testId} format="num" step="0.1" />;
}

function ValueInput({ label, derived, baseline, onCommit, disabled, testId, format, suffix, step }) {
  const asDisplay = (v) => {
    if (v === null || v === undefined) return "";
    if (format === "pct") return (v * 100).toFixed(1);
    return Number(v).toFixed(1);
  };
  const derivedText = asDisplay(derived);
  const [text, setText] = useState(derivedText);
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(derivedText); }, [derivedText, focused]);

  const commit = () => {
    setFocused(false);
    if (text === "" || Number.isNaN(parseFloat(text))) { setText(derivedText); return; }
    onCommit(text);
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] font-bold mb-2 flex items-center">
        <span>{label}</span>
        <DeltaChip delta={computeDelta(derived, baseline)} format={format} />
      </div>
      <div className="flex items-stretch">
        <input
          type="number"
          step={step}
          disabled={disabled}
          value={text}
          onFocus={() => setFocused(true)}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          className={`flex-1 bg-[#0f0f0f] border border-white/10 px-3 py-2 text-lg font-heading text-white focus:outline-none focus:border-[var(--org-accent)] text-center ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
          data-testid={testId}
        />
        {suffix && <div className="w-9 border border-white/10 border-l-0 bg-[#101010] flex items-center justify-center text-[#a0a0a0] text-xs">{suffix}</div>}
      </div>
    </div>
  );
}

function DeltaChip({ delta, format }) {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return null;
  const threshold = format === "pct" ? 0.001 : 0.005;
  if (Math.abs(delta) < threshold) return null;
  const up = delta > 0;
  const cls = up ? "text-[#4ade80]" : "text-[#f87171]";
  const sign = up ? "+" : "";
  const disp = format === "pct" ? `${sign}${(delta * 100).toFixed(1)}%` : `${sign}${delta.toFixed(2)}`;
  return <span className={`ml-2 text-[11px] font-bold ${cls}`}>{disp}</span>;
}

function computeDelta(current, baseline) {
  if (current === null || current === undefined) return null;
  if (baseline === null || baseline === undefined) return null;
  return Number(current) - Number(baseline);
}

function BigCard({ label, value, format, baseline, formula, testId, digits = 2 }) {
  const display = value === null || value === undefined
    ? "—"
    : format === "pct" ? `${(value * 100).toFixed(1)}%` : Number(value).toFixed(digits);
  return (
    <div className="border border-white/10 bg-[#101010] p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#a0a0a0] font-bold">{label}</div>
      <div className="h-0.5 w-8 bg-[var(--org-accent)] mt-2 mb-3" />
      <div className="flex items-baseline">
        <div className="font-heading text-4xl text-white">{display}</div>
        <DeltaChip delta={computeDelta(value, baseline)} format={format} />
      </div>
      <div className="text-[11px] text-[#757575] mt-3">{formula}</div>
    </div>
  );
}

function SmallCard({ label, value, format, baseline, testId, digits = 2 }) {
  const display = value === null || value === undefined
    ? "—"
    : format === "pct" ? `${(value * 100).toFixed(1)}%` : Number(value).toFixed(digits);
  return (
    <div className="border border-white/10 bg-[#101010] px-4 py-3 flex items-center justify-between" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] font-bold">{label}</div>
      <div className="flex items-baseline">
        <div className="font-heading text-2xl text-white">{display}</div>
        <DeltaChip delta={computeDelta(value, baseline)} format={format} />
      </div>
    </div>
  );
}
